/**
 * ClientMarginTab — Rentabilité (marge nette) par client, courbes multi-clients.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { RefreshCw, AlertTriangle, Search, TrendingDown, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2'];
const MAX_CLIENTS = 6;

const METRICS = {
  netMarginPct: { label: 'Marge nette %', suffix: ' %', fmt: (v) => (v === null || v === undefined ? '—' : `${v} %`) },
  netMargin: { label: 'Marge nette (XOF)', suffix: '', fmt: (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} XOF` },
  revenue: { label: "Chiffre d'affaires", suffix: '', fmt: (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} XOF` },
  opsCount: { label: "Nombre d'opérations", suffix: '', fmt: (v) => `${v || 0}` },
};

const compact = (v) => {
  const n = Math.abs(v || 0);
  if (n >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Md`;
  if (n >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} M`;
  if (n >= 1_000) return `${Math.round(v / 1_000)} k`;
  return `${Math.round(v || 0)}`;
};

const ClientMarginTab = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState('12');
  const [granularity, setGranularity] = useState('month');
  const [metric, setMetric] = useState('netMarginPct');
  const [showVolume, setShowVolume] = useState(true);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');

  const firstLoad = useRef(true);

  const fetchData = useCallback(async (clients) => {
    const list = clients || [];
    const useDefault = firstLoad.current && list.length === 0;
    // '__none__' = aucun client : on garde la sélection vide voulue par l'utilisateur
    const q = useDefault ? '' : (list.length ? list.join('|') : '__none__');
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/finance/cockpit/client-margin-series?months=${months}&granularity=${granularity}${q ? `&clients=${encodeURIComponent(q)}` : ''}`,
        { withCredentials: true },
      );
      firstLoad.current = false;
      setData(res.data);
      if (useDefault) {
        setSelected(res.data?.selectedClients || []);
      } else if (list.length) {
        // On élague les clients sans aucune donnée sur la fenêtre choisie
        const availableNames = new Set((res.data?.availableClients || []).map(c => c.name));
        const pruned = list.filter(c => availableNames.has(c));
        if (pruned.length !== list.length) setSelected(pruned);
      }
    } catch (e) {
      toast({ title: 'Erreur', description: 'Chargement de la rentabilité impossible', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [months, granularity]);

  useEffect(() => { fetchData(selected); /* eslint-disable-next-line */ }, [months, granularity]);

  // Couleur stable par client (identique dans la liste, le graphique et le tableau)
  const colorFor = useCallback(
    (name) => COLORS[Math.max(0, selected.indexOf(name)) % COLORS.length],
    [selected],
  );

  const toggleClient = (name) => {
    if (selected.includes(name)) {
      const next = selected.filter(c => c !== name);
      setSelected(next);
      fetchData(next);
      return;
    }
    if (selected.length >= MAX_CLIENTS) {
      toast({ title: 'Maximum atteint', description: `${MAX_CLIENTS} clients maximum pour rester lisible` });
      return;
    }
    const next = [...selected, name];
    setSelected(next);
    fetchData(next);
  };

  const chartData = useMemo(() => {
    if (!data?.periodLabels) return [];
    return data.periodLabels.map((label, i) => {
      const row = { label };
      (data.clients || []).forEach(c => {
        const p = c.series[i] || {};
        row[`${c.name}__metric`] = p[metric];
        row[`${c.name}__ops`] = p.opsCount;
      });
      return row;
    });
  }, [data, metric]);

  const filtered = (data?.availableClients || []).filter(
    c => !search || c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-4" data-testid="tab-client-margin">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Rentabilité par client</h2>
          <p className="text-sm text-gray-600">
            Marge nette = CA − coûts transporteur/contrat − frais annexes (Wave, quote-part frais bancaires).
            Alerte de décrochage : {data?.rule ? `−${data.rule.points} pts sur les ${data.rule.months} dernières périodes facturées` : '…'}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="margin-metric"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(METRICS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={granularity} onValueChange={(v) => { setGranularity(v); setMonths(v === 'week' ? '12' : '12'); }}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="margin-granularity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mensuel</SelectItem>
              <SelectItem value="week">Hebdomadaire</SelectItem>
            </SelectContent>
          </Select>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[160px] bg-white" data-testid="margin-periods"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(granularity === 'week' ? ['8', '12', '24'] : ['6', '12', '24']).map(p => (
                <SelectItem key={p} value={p}>{p} {granularity === 'week' ? 'semaines' : 'mois'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 px-2">
            <Switch checked={showVolume} onCheckedChange={setShowVolume} data-testid="margin-volume-toggle" />
            <span className="text-xs text-gray-600">Volume d'opérations</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchData(selected)} data-testid="margin-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {(data?.alerts || []).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="margin-alerts">
          <div className="flex items-center gap-2 text-red-800 font-medium text-sm mb-2">
            <AlertTriangle className="w-4 h-4" />Clients en décrochage de rentabilité
          </div>
          <div className="space-y-1">
            {data.alerts.map(a => (
              <div key={a.client} className="text-sm text-red-700" data-testid={`margin-alert-${a.client}`}>
                • {a.message} — marge actuelle {a.lastMarginPct} %
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm lg:col-span-1">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input className="pl-8 h-9 text-sm" placeholder="Rechercher un client" value={search}
                   onChange={(e) => setSearch(e.target.value)} data-testid="margin-client-search" />
          </div>
          <div className="text-xs text-gray-500 mb-2">{selected.length}/{MAX_CLIENTS} sélectionné(s)</div>
          <div className="max-h-[320px] overflow-y-auto space-y-1" data-testid="margin-client-list">
            {filtered.map((c, i) => {
              const checked = selected.includes(c.name);
              const color = colorFor(c.name);
              return (
                <button key={c.name} onClick={() => toggleClient(c.name)}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${checked ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                        data-testid={`margin-client-option-${i}`}>
                  <span className="flex items-center gap-2 truncate">
                    <span className="w-2.5 h-2.5 rounded-full border" style={{ background: checked ? color : 'transparent', borderColor: checked ? color : '#cbd5e1' }} />
                    <span className="truncate text-gray-800">{c.name}</span>
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">{compact(c.revenue)}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm lg:col-span-3">
          {loading && !data ? <Skeleton className="h-80 w-full" /> : (
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tickFormatter={metric === 'netMarginPct' ? (v) => `${v}%` : compact} tick={{ fontSize: 11 }} />
                {showVolume && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />}
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                         formatter={(v, n) => [n.endsWith('(ops)') ? `${v} op.` : METRICS[metric].fmt(v), n.replace('__metric', '')]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {showVolume && (data?.clients || []).map((c) => (
                  <Bar key={`${c.name}-ops`} yAxisId="right" dataKey={`${c.name}__ops`} name={`${c.name} (ops)`}
                       legendType="none" fill={colorFor(c.name)} fillOpacity={0.18} radius={[3, 3, 0, 0]} />
                ))}
                {(data?.clients || []).map((c) => (
                  <Line key={c.name} yAxisId="left" type="monotone" dataKey={`${c.name}__metric`} name={c.name}
                        stroke={colorFor(c.name)} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Synthèse sur la période</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="margin-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Client', 'CA', 'Marge nette', 'Marge %', 'Opérations', 'Marge / opération', 'Tendance'].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.clients || []).map((c, i) => (
                <tr key={c.name} className="border-b border-gray-100 hover:bg-gray-50/70" data-testid={`margin-row-${i}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: colorFor(c.name) }} />
                      <span className="font-medium text-gray-900">{c.name}</span>
                      {c.degrading && <Badge variant="destructive" className="text-[10px]">Décrochage</Badge>}
                      {c.costDataIncomplete && (
                        <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300"
                               title={`${c.opsMissingCost} opération(s) sans prix transporteur saisi`}>
                          Coûts incomplets
                        </Badge>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">{compact(c.totals.revenue)}</td>
                  <td className={`px-3 py-2.5 font-semibold ${c.totals.netMargin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{compact(c.totals.netMargin)}</td>
                  <td className="px-3 py-2.5 text-gray-700">{c.totals.netMarginPct === null ? '—' : `${c.totals.netMarginPct} %`}</td>
                  <td className="px-3 py-2.5 text-gray-700">{c.totals.opsCount}</td>
                  <td className="px-3 py-2.5 text-gray-700">{compact(c.totals.marginPerOp)}</td>
                  <td className="px-3 py-2.5">
                    {c.trendPoints === null || c.trendPoints === undefined ? <span className="text-gray-400">—</span> : (
                      <span className={`inline-flex items-center gap-1 ${c.trendPoints < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {c.trendPoints < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
                        {c.trendPoints} pts
                        {c.volumeChangePct !== null && c.volumeChangePct !== undefined && (
                          <span className="text-gray-500">· vol. {c.volumeChangePct > 0 ? '+' : ''}{c.volumeChangePct} %</span>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(data?.clients || []).length === 0 && !loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500" data-testid="margin-empty">Sélectionnez au moins un client</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientMarginTab;
